import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
	setChannelAction,
	subscribeChannelAction,
	unsubscribeChannelAction,
	setChannelHistoryAction,
	publishChannelAction,
	setWsChannelHistoryAction,
	sendChannelMessageAction,
	// clearChannelAction,
	setChannelParticipantsAction,
	setChannelNewMessagesAction,
	setReadTicketNotificationsAction,
	disconnectAction,
	participantSubscribedAction,
	participantUnsubscribedAction,
	modifyChannelAction,
	setWebsocketToken,
	getChannelMessagesAction,
	setSubscribingChannel,
	setGettingChannelMessages,
	setUnreadCount,
} from './actions/websocketsActions';
import { fetchTicketAction, setTicketRefReadAction, setTicketUnreadAction, incrementUnreadCount } from '../Tickets/actions/ticketsActions';
import soundfile from '../../assets/sounds/mention-alert.mp3';
import { handleBlockingUIAction, handlePersistentAlert } from '../Generic/actions/genericActions';
import eventsService from '../../helpers/Events';
import MESSAGES from '../../helpers/messages';
import { getTranslate } from 'react-localize-redux';
import { IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { dateToString, parseDateOffset } from '../../helpers/functions/functions';
import { NOTIFICATIONS } from '../../helpers/tables';
import { setTableCountAction, changeTablePageAction, setTableLoadinAction } from '../Generic/actions/tableActions';

import { withAppVisibility } from './withAppVisibility';

export const SocketContext = React.createContext();

const READY = { CONNECTING: 0, OPEN: 1, CLOSING: 2, CLOSED: 3 };

class SocketProvider extends Component {
	BACKOFF_BASE_MS = 500;
	BACKOFF_MAX_MS = 2000;
	HEARTBEAT_MS = 5000;
	PONG_TIMEOUT_MS = 30000;

	reconnectAttempts = 0;
	reconnectTimer = null;
	heartbeatTimer = null;
	pendingPongTimer = null;

	constructor() {
		super();
		this.state = {
			ws: null,
			token: null,
			attempts: 5,
			subscribing: [],
			unsubscribing: [],
			close_code: null,
			retry_connect: true,
		};

		this.audio = new Audio(soundfile);
	}

	componentDidMount() {
		if (!this.props.login) return;
		this.connect();
	}

	componentWillUnmount() {
		this.clearTimers();
		try {
			this.state.ws && this.state.ws.close(1000, 'unmount');
		} catch {}
	}

	componentDidUpdate(prev) {
		if (!prev.login && this.props.login) {
			console.debug('SocketProvider: componentDidUpdate: login');
			this.tryImmediateReconnect('login');
		} else if (this.props.login && !prev.isVisible && this.props.isVisible) {
			console.debug('SocketProvider: componentDidUpdate: visible');
			this.tryImmediateReconnect('visible');
		} else if (this.props.login && !prev.isFocused && this.props.isFocused) {
			console.debug('SocketProvider: componentDidUpdate: focus');
			this.tryImmediateReconnect('focus');
		} else if (this.props.login && !prev.isOnline && this.props.isOnline) {
			console.debug('SocketProvider: componentDidUpdate: online');
			this.tryImmediateReconnect('online');
		} else if (prev.login && !this.props.login) {
			console.debug('SocketProvider: componentDidUpdate: logged out');
			this.clearTimers();
			try {
				this.state.ws && this.state.ws.close(1000, 'unmount');
			} catch {}
		}
	}

	scheduleReconnect = (reason) => {
		const base = this.BACKOFF_BASE_MS * Math.pow(2, this.reconnectAttempts);
		const delay = Math.min(base, this.BACKOFF_MAX_MS);
		const jitter = Math.floor(delay * 0.2 * Math.random()); // ±20% jitter
		const wait = delay - jitter;
		this.reconnectAttempts += 1;

		if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
		this.reconnectTimer = setTimeout(() => {
			this.connect();
		}, wait);
		console.debug(`SocketProvider: Reconnecting in ${wait}`);
	};

	tryImmediateReconnect = (reason) => {
		const rs = this.ws ? this.ws.readyState : READY.CLOSED;
		if (rs !== READY.OPEN) {
			if (this.reconnectTimer) {
				clearTimeout(this.reconnectTimer);
				this.reconnectTimer = null;
			}
			this.connect();
		}
	};

	clearTimers = () => {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
		this.stopHeartbeat();
	};

	startHeartbeat = () => {
		this.stopHeartbeat();
		this.heartbeatTimer = setInterval(() => {
			console.debug('SocketProvider: Heartbeat');
			if (!this.state.ws || this.state.ws.readyState !== READY.OPEN) {
				this.tryImmediateReconnect('heartbeat-missed');
				return;
			}

			try {
				this.state.ws.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
			} catch {
				console.debug('SocketProvider: Failed to send ping');
				this.tryImmediateReconnect('send-failed');
				return;
			}

			if (this.pendingPongTimer) clearTimeout(this.pendingPongTimer);

			this.pendingPongTimer = setTimeout(() => {
				console.debug('SocketProvider: Pong timer triggered');
				try {
					this.state.ws && this.state.ws.close(4000, 'pong-timeout');
				} catch {}
			}, this.PONG_TIMEOUT_MS);
		}, this.HEARTBEAT_MS);
	};

	stopHeartbeat = () => {
		if (this.heartbeatTimer) {
			clearInterval(this.heartbeatTimer);
			this.heartbeatTimer = null;
		}
		if (this.pendingPongTimer) {
			clearTimeout(this.pendingPongTimer);
			this.setState({ pendingPongTimer: null });
		}
	};

	connect = async () => {
		if (this.state.ws && (this.state.ws.readyState === READY.OPEN || this.state.ws.readyState === READY.CONNECTING)) {
			return;
		}

		console.debug('SocketProvider: connecting');

		this.clearTimers();

		await this.props.setWebsocketToken();

		const ws = new WebSocket(this.props.config.websocket_protocol + `://` + this.props.config.websockethost + '?token=' + this.props.token);

		const timer = setTimeout(() => {
			if (ws.readyState === WebSocket.CONNECTING) {
				console.debug('SocketProvider: aborting connect');
				ws.close();
			}
		}, 2000);

		ws.onopen = () => {
			clearTimeout(timer);
			console.debug('SocketProvider: connected');
			this.reconnectAttempts = 0;
			this.startHeartbeat();

			this.setState({ ws: ws }, () => {
				if (this.props.subscribed_channels.length > 0) {
					this.resubscribe();
				} else {
					this.props.handleBlockingUIAction(false, 'block_ui_reconnecting');
				}
			});
		};

		ws.onclose = (e) => {
			console.debug('SocketProvider: closing');
			this.stopHeartbeat();
			if (this.props.login) {
				this.scheduleReconnect('close ' + e.code);
			}
		};

		ws.onerror = (err) => {
			console.debug('SocketProvider: connection failed', { cause: err });
			ws.close();
		};

		ws.onmessage = (msg) => {
			var data = JSON.parse(msg.data);

			if (data.type === 'pong') {
				clearTimeout(this.pendingPongTimer);
			}

			if (data.type === 'subscribe' && data.sender.id == this.props.user.id) {
				this.props.subscribeChannelAction(
					data.channel,
					data.created,
					data.participants,
					data.ref_type,
					data.ref_id,
					data.title,
					data.unread_count,
					(channel) => {
						setTimeout(() => {
							if (channel === this.props.user.channel?.name) {
								this.props.setTableLoadinAction(NOTIFICATIONS, true);
								this.props.changeTablePageAction(NOTIFICATIONS, 0);
							}
							this.setSubscribed(channel);
							var before_dt =
								parseDateOffset(new Date(new Date().toLocaleString('en-US', { timeZone: this.props.timezone })))
									.toISOString()
									.split('.')[0] + 'Z';
							before_dt = before_dt.replace('T', ' ').replace('Z', '');
							this.getMessages(
								channel,
								{
									cnt: channel === this.props.user?.channel?.name ? this.props.tables_settings[NOTIFICATIONS]?.rows_per_page[0] : 20,
									before_dt,
								},
								'replace'
							);
						}, 1000);
					}
				);
				// TODO: call getMessages instead of using history from subscribe message
			} else if (data.type === 'subscribe' && data.sender.id != this.props.user.id) {
				this.props.participantSubscribedAction(data.channel, data.sender);
			} else if (data.type === 'unsubscribe' && data.sender.id == this.props.user.id) {
				this.props.unsubscribeChannelAction(data.channel, this.setUnsubscribed);
			} else if (data.type === 'unsubscribe' && data.sender.id != this.props.user.id && this.props.login) {
				this.props.participantUnsubscribedAction(data.channel, data.sender);
			}

			// TODO: We need to define if readmark should a type of message or not (api listenner assume the readmark type)
			// if (data.type === 'readmark') {
			// 	data.type = 'publish';
			// 	data.link_type = 'readmark';
			// }

			if (data.type === 'publish' || data.type === 'notification') {
				// Populate information of unread and ref in order to use markread functionality
				if (typeof data.unread === 'undefined') {
					data.unread = data.sender.id != this.props.user.id ? '1' : '0';
				}
				if (typeof data.ref_type === 'undefined') {
					data.ref_type = MESSAGES.REF.CHAT_MESSAGES;
					data.ref_id = data.id;
				}

				this.props.publishChannelAction(data);

				if (this.props.websockets.channels[data.channel].callback) {
					this.props.websockets.channels[data.channel].callback();
				}
			}

			if (data.type === 'modifymessage') {
				this.props.modifyChannelAction(data);
			}

			if (data.type === 'getmessages') {
				this.props.getChannelMessagesAction(data.channel, data.messages, data.schedule_messages, (messages) => {
					if (data.channel === this.props.user.channel.name) {
						this.props.setTableLoadinAction(NOTIFICATIONS, false);
						this.props.setTableCountAction(NOTIFICATIONS, data.messages.total);
					}
				});
			}

			// To be improved given that in some situations there no need to update the ticket info (ex: patient on chat layout)
			if (this.props.websockets.channels?.[data.channel]) {
				if (this.props.websockets.channels?.[data.channel].ref_type === MESSAGES.ORIG.TICKETS) {
					if ((data.type == 'publish' || data.type == 'modifymessage') && data.link_type != 'readmark') {
						this.props.fetchTicketAction(this.props.websockets.channels?.[data.channel].ref_id, false);
					} else if (data.link_type == 'readmark') {
						// Update Unreads list
						// TODO: fix this trigger, request unread_count on ws control messages
						let unreads = this.props.websockets.channels[data.channel].messages.items.filter((message) => {
							return message.unread == '1';
						});
						unreads = unreads.map((item) => ({ id: item?.id, ref_type: item?.ref_type, ref_id: item?.ref_id }));
						this.props.setTicketUnreadAction(unreads);
					}
				}
			}

			if (data.type === 'control' && data.link_type === 'readmark') {
				this.props.setUnreadCount(data.channel, 'subtract');

				switch (data.origin_type) {
					case MESSAGES.ORIG.TICKETS:
						if (data?.messages_ids) {
							this.props.setTicketRefReadAction(data.ref_type, data.messages_ids);
						} else {
							if (data.ref_type) {
								this.props.setTicketRefReadAction(data.ref_type);
							}
						}
						break;
					default:
						break;
				}
			}

			if (data.type !== 'control' && data.unread == 1) {
				this.props.setUnreadCount(data.channel, 'add');
				this.props.incrementUnreadCount(data.origin_id, 1);
			}
		};
	};

	resubscribe = () => {
		if (this.props.subscribed_channels.length > 0) {
			this.props.subscribed_channels.forEach((channel, index) => {
				if (!this.props.websockets.channels?.[channel]) {
					setTimeout(() => {
						this.subscribe(channel);
						if (this.props.subscribed_channels.length === index + 1) {
							this.props.handleBlockingUIAction(false, 'block_ui_reconnecting');
						}
					}, 500);
				}
			});
		}
	};

	setSubscribed = (channel_name) => {
		this.setState({
			subscribing: this.state.subscribing.filter((name) => name !== channel_name),
		});
	};

	setUnsubscribed = (channel_name) => {
		this.setState({
			unsubscribing: this.state.unsubscribing.filter((name) => name !== channel_name),
		});
	};

	subscribe = (channel = null) => {
		const { ws } = this.state;

		if (!this.props.websockets.channels[channel]) {
			this.props.setSubscribingChannel(channel);
		}

		setTimeout(() => {
			if (!this.state.subscribing.includes(channel) && !this.props.websockets.channels[channel]) {
				this.setState(
					{
						subscribing: [...this.state.subscribing, channel],
					},
					() => {
						ws.send(
							JSON.stringify({
								type: 'subscribe',
								channel,
							})
						);
					}
				);
			}
		}, 1000);
	};

	getMessages = (channel = null, params = {}, position = 'before') => {
		const { ws } = this.state;

		if (channel === this.props.user.channel.name) {
			this.props.setTableLoadinAction(NOTIFICATIONS, true);
		}

		this.props.setGettingChannelMessages(channel, position);

		setTimeout(() => {
			ws.send(
				JSON.stringify({
					type: 'getmessages',
					channel,
					...params,
					// cnt: 1,
					// after_cnt: 4
				})
			);
		}, 500);
	};

	unsubscribe = (channel = null) => {
		const { ws } = this.state;

		if (!this.state.unsubscribing.includes(channel) && this.props.websockets.channels[channel]) {
			this.setState(
				{
					unsubscribing: [...this.state.unsubscribing, channel],
				},
				() => {
					ws.send(
						JSON.stringify({
							type: 'unsubscribe',
							channel,
						})
					);
				}
			);
		}
	};

	publish = (channel = null, data, callback = false) => {
		const { ws } = this.state;

		ws.send(
			JSON.stringify({
				type: 'publish',
				channel,
				...data,
			})
		);

		if (callback) {
			callback();
		}
	};

	modify = (channel = null, message_id, text = '') => {
		const { ws } = this.state;

		ws.send(
			JSON.stringify({
				type: 'modifymessage',
				channel,
				message_id,
				text,
			})
		);
	};

	render() {
		return (
			<SocketContext.Provider
				value={{
					ws: this.state.ws,
					subscribe: this.subscribe,
					unsubscribe: this.unsubscribe,
					publish: this.publish,
					modify: this.modify,
					getMessages: this.getMessages,
				}}
			>
				{this.props.children}
			</SocketContext.Provider>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	translate: getTranslate(state.localize),
	user: state.users.whoami,
	config: state.config,
	timezone: state.users.whoami.timezone,
	websockets: state.websockets,
	token_expires: state.websockets.token_expires,
	token: state.websockets.token,
	login: state.login.login,
	subscribed_channels: state.websockets.subscribed_channels,
	blocking_ui: state.generic.blocking_ui,
	tables_settings: state.settings?.site?.tables,
});

export default connect(mapStateToProps, {
	setChannelAction,
	subscribeChannelAction,
	unsubscribeChannelAction,
	publishChannelAction,
	setChannelHistoryAction,
	setWsChannelHistoryAction,
	sendChannelMessageAction,
	fetchTicketAction,
	setTicketUnreadAction,
	setChannelParticipantsAction,
	setChannelNewMessagesAction,
	setReadTicketNotificationsAction,
	disconnectAction,
	participantSubscribedAction,
	participantUnsubscribedAction,
	modifyChannelAction,
	handleBlockingUIAction,
	setWebsocketToken,
	handlePersistentAlert,
	getChannelMessagesAction,
	setSubscribingChannel,
	setGettingChannelMessages,
	setTableCountAction,
	changeTablePageAction,
	setTableLoadinAction,
	setUnreadCount,
	setTicketRefReadAction,
	incrementUnreadCount,
})(withAppVisibility(SocketProvider));

SocketProvider.propTypes = {
	channel_name: PropTypes.string,
	channel: PropTypes.string,
	handleSendMessage: PropTypes.func,
	customFirstMessage: PropTypes.func,
	handleFilterMessages: PropTypes.func,
	token: PropTypes.string,
	token_expires: PropTypes.string,
};
